import Loader from '@/components/Loader';
import MaterialCard from '@/components/MaterialCard';
import { VideoModal } from '@/components/VideoModal';
import BaseLayout from '@/layout/BaseLayout';
import { trpc, trpcClient } from '@/trpcClient';
import { cn } from '@/utils/utils';
import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MaterialListFilter } from './MaterialListFilter';
import {
  fromFilterValues,
  readListState,
  toFilterValues,
  validateListState,
  writeListState,
  type MaterialListUrlState,
} from './listState';
import { SCROLL_RESTORE_MAX_AGE_MS, saveScroll, takeSavedScroll } from './scrollRestore';
import { SummaryDashboard, SummaryProps } from '@/components/SummaryDash';
import cloudOffSvg from '@/assets/icons-normalized/cloud-off.svg?raw';
import onlineSvg from '@/assets/icons-normalized/online.svg?raw';
import clockZzSvg from '@/assets/icons-normalized/clock-zz.svg?raw';
import { MaterialTable } from '@/components/MaterialTable';
import { MaterialDto } from '@shelflix/trpc/routes/material/list/useCase';

const MATERIAL_TABLE_PAGE_SIZE = 10;
const MATERIAL_GRID_PAGE_SIZE = 24;
const MATERIAL_GRID_MIN_FACILITIES = 2;

export function MaterialList() {
  const location = useLocation();
  const [pendingScroll, setPendingScroll] = useState(() => takeSavedScroll(location.pathname + location.search));
  const hasShownToast = useRef(false);
  const [videoUrls, setVideoUrls] = useState<string[]>([]);
  const videoTriggerRef = useRef<HTMLButtonElement | null>(null);

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const lastWritten = useRef(searchParams.toString());
  const [filterKey, setFilterKey] = useState(0);

  const catalogQuery = useQuery(trpc.dashboard.filters.queryOptions({}));
  const urlState = readListState(searchParams);
  const { valid: state, rejected } = catalogQuery.data
    ? validateListState(urlState, catalogQuery.data)
    : { valid: catalogQuery.isError ? urlState : undefined, rejected: [] };
  const filters = state ? toFilterValues(state) : {};

  const writeState = (next: MaterialListUrlState) => {
    const params = writeListState(next);
    lastWritten.current = params.toString();
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const current = searchParams.toString();
    if (current !== lastWritten.current) {
      lastWritten.current = current;
      setFilterKey((key) => key + 1);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!state) return;
    rejected.forEach((label) => {
      const message = `Filtro ${label} não pôde ser aplicado`;
      toast.warning(message, { id: message });
    });
    if (writeListState(state).toString() !== searchParams.toString()) writeState(state);
  }, [state?.search, state?.material, state?.brand, state?.state, state?.status, state?.page, searchParams]);

  const [displayListAsGrid, setDisplayListAsGrid] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const savedView = window.localStorage.getItem('material-list-view-mode');
    if (savedView === 'table') return false;
    return true; // default: grid
  });

  const tableQuery = useQuery({
    ...trpc.material.list.queryOptions({
      page: state?.page ?? 1,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
      recordsPerPage: MATERIAL_TABLE_PAGE_SIZE,
    }),
    enabled: !!state && !displayListAsGrid,
    placeholderData: keepPreviousData,
    refetchInterval: 180_000, // 3 minutes — keep status in sync with device heartbeats
    refetchIntervalInBackground: false,
  });

  const gridInput = {
    ...(filters.search?.value && { search: { ...filters.search } }),
    ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
    sort: { field: 'facilityName' as const, direction: 'asc' as const },
    recordsPerPage: MATERIAL_GRID_PAGE_SIZE,
  };
  const gridQuery = useInfiniteQuery({
    queryKey: [...trpc.material.list.pathKey(), { input: gridInput, type: 'infinite' }],
    queryFn: ({ pageParam }) => trpcClient.material.list.query({ ...gridInput, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPageParam * MATERIAL_GRID_PAGE_SIZE < lastPage.total ? lastPageParam + 1 : undefined,
    enabled: !!state && displayListAsGrid,
    gcTime: SCROLL_RESTORE_MAX_AGE_MS,
    refetchInterval: 180_000,
    refetchIntervalInBackground: false,
  });

  const { isLoading } = displayListAsGrid ? gridQuery : tableQuery;
  // A failed next page keeps the loaded cards on screen
  const isError = displayListAsGrid ? gridQuery.isError && !gridQuery.data : tableQuery.isError;
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = gridQuery;
  const { data: tableData, isFetching: isFetchingTable } = tableQuery;

  // Map insertion order keeps the cards in the order the server sorted the materials
  const groupedMaterials = useMemo(() => {
    const byUuid = new Map<string, MaterialDto>();
    gridQuery.data?.pages.forEach((page) => page.materials.forEach((m) => byUuid.set(m.uuid, m)));
    const groups = new Map<string, MaterialDto[]>();
    byUuid.forEach((material) => {
      const group = groups.get(material.facility.uuid);
      if (group) group.push(material);
      else groups.set(material.facility.uuid, [material]);
    });
    return Array.from(groups.values());
  }, [gridQuery.data]);

  const loadedGridPages = gridQuery.data?.pages.length ?? 0;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && fetchNextPage(), {
      rootMargin: '800px',
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    loadedGridPages,
    displayListAsGrid,
    isLoading,
  ]);

  // The grid has two columns, so a single large facility would leave the second one empty until the sentinel fires
  const facilityCount = groupedMaterials.length;
  useEffect(() => {
    if (
      facilityCount > 0 &&
      facilityCount < MATERIAL_GRID_MIN_FACILITIES &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isFetchNextPageError
    )
      fetchNextPage({ cancelRefetch: false });
  }, [facilityCount, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  useEffect(() => {
    if (!pendingScroll) return;
    if (!displayListAsGrid || (gridQuery.isError && !gridQuery.data)) {
      setPendingScroll(undefined);
      return;
    }
    if (!state || !gridQuery.data) return;
    if (loadedGridPages < pendingScroll.pages && hasNextPage && !isFetchNextPageError) {
      if (!isFetchingNextPage) fetchNextPage({ cancelRefetch: false });
      return;
    }
    window.scrollTo(0, pendingScroll.scrollY);
    setPendingScroll(undefined);
  }, [
    pendingScroll,
    displayListAsGrid,
    !!state,
    gridQuery.data,
    gridQuery.isError,
    loadedGridPages,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  ]);

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success(`Alteração salva com sucesso${location.state.code ? ' (' + location.state.code + ')' : ''}`, {
        duration: 10000,
      });
      hasShownToast.current = true;

      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleOpenVideoModal = async (materialId: string) => {
    try {
      const response = await trpcClient.material.get.query({
        uuid: materialId,
      });
      if (!response.currentCampaign) {
        toast.error('Material não está associado a nenhuma campanha ativa no momento.');
        return;
      }
      setVideoUrls(response.currentCampaign.medias.map((item) => item.mediaUrl));
      setTimeout(() => {
        videoTriggerRef.current?.click();
      }, 0);
    } catch (err) {
      toast.error(`Erro ao buscar vídeos: ${err}`);
    }
  };

  //Data for Summary Dashboard
  const {
    data: countByStatus = [],
    isLoading: _isLoadingCountByStatus,
    isError: _isErrorCountByStatus,
  } = useQuery({
    ...trpc.material.count.queryOptions({
      grouping: 'status',
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
    }),
    enabled: !!state,
    placeholderData: keepPreviousData,
  });

  const statusArray = Array.isArray(countByStatus) ? countByStatus : [];
  const materialSumarryData: SummaryProps = {
    overview: {
      title: 'Total',
      amount: statusArray
        .filter(
          (item) =>
            item.group?.toLowerCase() === 'active' ||
            item.group?.toLowerCase() === 'operationalonline' ||
            item.group?.toLowerCase() === 'offline'
        )
        .reduce((sum, item) => sum + (item.count || 0), 0),
    },
    summary: [
      {
        title: 'Online',
        count: statusArray
          .filter((item) => item.group?.toLowerCase() === 'active' || item.group?.toLowerCase() === 'operationalonline')
          .reduce((sum, item) => sum + (item.count || 0), 0),
        statusFilter: 'ACTIVE',
        icon: onlineSvg,
      },
      {
        title: 'Offline',
        count: statusArray.find((item) => item.group?.toLowerCase() === 'offline')?.count || 0,
        statusFilter: 'OFFLINE',
        icon: cloudOffSvg,
      },
      {
        title: 'Indisponível',
        count: statusArray
          .filter((item) => item.group?.toLowerCase() === 'maintenance' || item.group?.toLowerCase() === 'standby')
          .reduce((sum, item) => sum + (item.count || 0), 0),
        statusFilter: 'MAINTENANCE',
        icon: clockZzSvg,
      },
    ],
  };

  //Filters
  const {
    data: filtersData = { state: [], brand: [], material: [] },
    isLoading: _isLoadingFilters,
    isFetching: _isFetchingFilters,
    isError: _isErrorFilters,
  } = useQuery({
    ...trpc.dashboard.filters.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    enabled: !!state,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('material-list-view-mode', displayListAsGrid ? 'grid' : 'table');
  }, [displayListAsGrid]);

  const setCurrentPage = (page: number) => state && writeState({ ...state, page });

  useEffect(() => {
    if (!state || !tableData || isFetchingTable || displayListAsGrid) return;
    if (state.page > Math.max(1, Math.ceil(tableData.total / MATERIAL_TABLE_PAGE_SIZE))) setCurrentPage(1);
  }, [tableData, isFetchingTable, displayListAsGrid, state?.page]);

  return (
    <BaseLayout>
      <section className="flex flex-1 flex-col gap-6">
        <SummaryDashboard summaryData={materialSumarryData} enableHorizontal={true} redirect={false} />
        {state && (
          <MaterialListFilter
            key={filterKey}
            onChange={(values) => {
              const next = fromFilterValues(values);
              if (writeListState(next).toString() !== writeListState({ ...state, page: 1 }).toString())
                writeState(next);
            }}
            initialValue={filters.search}
            initialFilters={filters.filter}
            states={filtersData.state}
            brands={filtersData.brand}
            materials={filtersData.material}
            displayListAsGrid={displayListAsGrid}
            setDisplayListAsGrid={(value) => {
              setDisplayListAsGrid(value);
              setCurrentPage(1);
            }}
          />
        )}
        <section className="h-full w-full">
          {(!state || isLoading) && <Loader message="Carregando materiais de PDV..." />}
          {isError && <p className="p-4 text-red-500">Erro ao carregar os materiais de PDV.</p>}
          {state &&
            !isLoading &&
            !isError &&
            (displayListAsGrid ? (
              <div className={cn('grid justify-start gap-3 px-2 min-[1040px]:grid-cols-2')}>
                {groupedMaterials.map((materials) => (
                  <MaterialCard
                    key={materials[0].facility.uuid}
                    materials={materials}
                    establishment={materials[0].facility.name}
                    handleOpenVideoModal={handleOpenVideoModal}
                    onOpen={() => saveScroll(loadedGridPages, location.pathname + location.search)}
                  />
                ))}
                <div ref={sentinelRef} className="col-span-full h-px" />
              </div>
            ) : (
              <MaterialTable
                currentPage={state?.page ?? 1}
                setCurrentPage={setCurrentPage}
                data={tableData?.materials || []}
                total={tableData?.total ?? 0}
              />
            ))}
        </section>

        <VideoModal videos={videoUrls} trigger={<button ref={videoTriggerRef} className="hidden" />} />
      </section>
    </BaseLayout>
  );
}
